/* eslint-disable no-nested-ternary */

import React, { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PseudoStates, Table } from '@cordata/buffet';
import PropTypes from 'prop-types';

import useSlice from '~/utils/slice/hook';
import useReferenceData from '~/utils/reference-data/hook';
import useApi from '~/utils/api/hook';

const PAGE_SIZE = 50;

const SliceCell = ({ data }) => {
  const [info, setInfo] = useState({});

  useEffect(() => {
    data
      .then(setInfo);
  }, []);

  return (
    <div className={`is-${info.type}`}>
      <span className="dot rounded-circle mr-2" />
      {info.type}{' '}
      {info.lineage && (<span className="text-muted">( <em>{info.lineage && ` ${info.lineage.join(', ')}`}</em> )</span>)}
    </div>
  );
};

SliceCell.propTypes = {
  data: PropTypes.shape().isRequired,
};

const ReferenceDataCollection = ({
  onClick,
  tableState,
  setTableState,
  refreshData,
}) => {
  const [data, setData] = useState([]);
  const { type, meta: { nameAttr, labelAttr, typeAttr } } = useReferenceData();
  const {
    currentSlice,
    setCurrentSlice,

    getInfo,
    fetchAll,
  } = useSlice();
  const { config } = useApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [pages, setPages] = useState();
  const [pgSize, setPageSize] = useState(PAGE_SIZE);
  const [defaultSorted, setDefaultSorted] = useState();

  const requestData = ({
    pageSize = PAGE_SIZE, page = 0, sorted = [], filtered = [],
  }) => config
    .get(type, {
      params: {
        sort: sorted.map(s => `${s.desc ? '-' : ''}${s.id}`).join(','),
        $slice_type: currentSlice.type,
        $slice_id: currentSlice.id,
        'page-size': pageSize,
        page: page + 1,
        ...filtered.reduce((a, m) => ({
          ...a,
          [m.id]: m.value,
        }), {}),
      },
    })
    .then(r => r.data);

  const fetchData = (state) => {
    setError(null);
    setData([]);

    setLoading(true);

    requestData(state)
      .then((r) => {
        setData(r.data);
        setPages(Math.ceil(r.total / (state.pageSize || PAGE_SIZE)));
        setPageSize(state.pageSize);
      })
      .catch(setError)
      .then(() => setLoading(false));
  };

  useEffect(() => {
    if (labelAttr) {
      const sorted = [{
        id: labelAttr,
      }, {
        id: 'isActive',
        desc: true,
      }, {
        id: 'defaultSortOrder',
      }];

      setDefaultSorted(sorted);
    }
  }, [labelAttr]);

  useEffect(() => {
    if (defaultSorted && currentSlice.type) {
      fetchData({ sorted: defaultSorted });
    }
  }, [currentSlice]);

  useEffect(() => {
    if (refreshData) {
      fetchData(tableState);
    }
  }, [refreshData]);

  useEffect(() => {
    fetchAll();
    setCurrentSlice('all');
  }, []);

  const columns = [{
    id: 'slice',
    Header: 'Slice',
    accessor: item => getInfo(item),
    Cell: c => <SliceCell data={c.value} />,
    filterable: false,
    sortable: false,
  }, {
    id: labelAttr,
    Header: 'Label',
    accessor: item => item.attributes[labelAttr],
  }, {
    id: nameAttr,
    Header: 'Name',
    accessor: item => item.attributes[nameAttr],
  }];

  if (typeAttr) {
    columns.push({
      id: typeAttr,
      Header: 'Type',
      accessor: item => item.attributes[typeAttr],
    });
  }

  columns.push({
    id: 'isActive',
    Header: 'Is Active',
    accessor: 'attributes.isActive',
    minWidth: 25,
    filterable: false,
    Cell: c => c.value && <div className="text-success text-center"><FontAwesomeIcon icon="check" /></div>,
  });

  return !error && defaultSorted ?
    <Table
      data={data}
      columns={columns}
      defaultSorted={defaultSorted}
      pages={pages}
      loading={loading}
      pageSize={pgSize}
      manual
      filterable
      onFetchData={debounce(
        (s) => {
          setTableState(s);
          fetchData(s);
        }
        , 750,
      )}
      getTrProps={(state, row) => ({
        style: {
          cursor: 'pointer',
        },
        className: `is-${row && row.row.slice.type}`,
        onClick: (e, handleOriginal) =>
          onClick(e, handleOriginal, row),
      })}
    />
    : <PseudoStates error={error} isLoading={loading} />;
};

ReferenceDataCollection.propTypes = {
  onClick: PropTypes.func.isRequired,
  tableState: PropTypes.shape().isRequired,
  setTableState: PropTypes.func.isRequired,
  refreshData: PropTypes.number,
};

ReferenceDataCollection.defaultProps = {
  refreshData: 0,
};

export default ReferenceDataCollection;
